import { Theme } from '@mui/material';

import { FONT_FAMILY } from '../consts';
import { ComponentsMuiTypography } from '../types';

// https://www.figma.com/design/4nTOFTtmTmvTmhW9tfnXO7/Ankr-Design-System-2.0?node-id=2551-37211&t=14XK277vjOEPEYQ8-0
export function getMuiTypography(theme: Theme): ComponentsMuiTypography {
  return {
    defaultProps: {
      fontFamily: FONT_FAMILY,
      variantMapping: {
        h1: 'h1',
        h2: 'h2',
        h3: 'h3',
        h4: 'h4',
        h5: 'h5',
        h6: 'h6',
        subtitle1: 'span',
        subtitle2: 'span',
        subtitle3: 'span',
        body1: 'span',
        body2: 'span',
        body3: 'span',
        body4: 'span',
        button1: 'span',
        button2: 'span',
        button3: 'span',
        caption: 'span',
      },
    },
    styleOverrides: {
      root: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        fontFeatureSettings: "'ss01' on, 'case' on, 'salt' on, 'calt' off",
      },
      h1: {
        fontWeight: 700,
        fontSize: 73,
        lineHeight: 1.05,
        letterSpacing: '-0.04em',

        [theme.breakpoints.down('sm')]: {
          fontSize: 56,
          letterSpacing: '-0.03em',
        },

        [theme.breakpoints.down('xs')]: {
          fontSize: 42,
          lineHeight: 1.1,
          letterSpacing: '-0.04em',
        },
      },

      h2: {
        fontWeight: 700,
        fontSize: 63,
        lineHeight: 1.05,
        letterSpacing: '-0.04em',

        [theme.breakpoints.down('sm')]: {
          fontSize: 49,
          letterSpacing: '-0.03em',
        },

        [theme.breakpoints.down('xs')]: {
          fontSize: 38,
          lineHeight: 1.1,
          letterSpacing: '-0.04em',
        },
      },

      h3: {
        fontWeight: 700,
        fontSize: 52,
        lineHeight: 1.05,
        letterSpacing: '-0.04em',

        [theme.breakpoints.down('sm')]: {
          fontSize: 42,
          letterSpacing: '-0.03em',
        },

        [theme.breakpoints.down('xs')]: {
          fontSize: 35,
          letterSpacing: '-0.04em',
        },
      },

      h4: {
        fontWeight: 700,
        fontSize: 42,
        lineHeight: 1.05,
        letterSpacing: '-0.04em',

        [theme.breakpoints.down('sm')]: {
          fontSize: 35,
          lineHeight: 1.1,
          letterSpacing: '-0.03em',
        },

        [theme.breakpoints.down('xs')]: {
          fontSize: 31,
          letterSpacing: '-0.03em',
        },
      },

      h5: {
        fontWeight: 700,
        fontSize: 35,
        lineHeight: 1.1,
        letterSpacing: '-0.04em',

        [theme.breakpoints.down('sm')]: {
          fontSize: 31,
          letterSpacing: '-0.02em',
        },

        [theme.breakpoints.down('xs')]: {
          fontSize: 28,
          lineHeight: 1.15,
          letterSpacing: '-0.03em',
        },
      },

      h6: {
        fontWeight: 700,
        fontSize: 28,
        lineHeight: 1.1,
        letterSpacing: '-0.03em',

        [theme.breakpoints.down('sm')]: {
          letterSpacing: '-0.02em',
        },

        [theme.breakpoints.down('xs')]: {
          lineHeight: 1.15,
          fontSize: 24,
        },
      },

      subtitle1: {
        fontWeight: 700,
        fontSize: 20,
        lineHeight: 1.35,
        letterSpacing: '-0.02em',

        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
        },
      },

      subtitle2: {
        fontWeight: 700,
        fontSize: 16,
        lineHeight: 1.35,
        letterSpacing: '-0.01em',

        [theme.breakpoints.down('xs')]: {
          fontSize: 14,
        },
      },

      subtitle3: {
        fontWeight: 700,
        fontSize: 14,
        lineHeight: 1.35,

        [theme.breakpoints.down('xs')]: {
          fontSize: 12,
          letterSpacing: '-0.02em',
        },
      },

      body1: {
        fontWeight: 400,
        fontSize: 20,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',

        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
        },
      },

      body2: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',

        [theme.breakpoints.down('xs')]: {
          fontSize: 14,
        },
      },

      body3: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: 1.4,

        [theme.breakpoints.down('xs')]: {
          fontSize: 12,
        },
      },

      body4: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: 1.35,

        [theme.breakpoints.down('xs')]: {
          fontSize: 11,
        },
      },
    },
    variants: [
      {
        props: { variant: 'subtitle3' },
        style: {
          fontWeight: 700,
          fontSize: 14,
          lineHeight: 1.35,
          letterSpacing: '0.01em',

          [theme.breakpoints.down('xs')]: {
            fontSize: 12,
            letterSpacing: '-0.02em',
          },
        },
      },
      {
        props: { variant: 'body3' },
        style: {
          fontWeight: 400,
          fontSize: 14,
          lineHeight: 1.4,

          [theme.breakpoints.down('xs')]: {
            fontSize: 12,
          },
        },
      },
      {
        props: { variant: 'body4' },
        style: {
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 1.35,

          [theme.breakpoints.down('xs')]: {
            fontSize: 11,
          },
        },
      },
      {
        props: { variant: 'button1' },
        style: {
          fontWeight: 600,
          fontSize: 20,
          lineHeight: 1.4,
        },
      },
      {
        props: { variant: 'button2' },
        style: {
          fontWeight: 600,
          fontSize: 16,
          lineHeight: 1.5,
        },
      },
      {
        props: { variant: 'button3' },
        style: {
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 1.43,
          letterSpacing: '0.01em',
        },
      },
      {
        props: { variant: 'caption' },
        style: {
          fontWeight: 400,
          fontSize: 9,
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
        },
      },
    ],
  };
}
